/**
 * VendorPortalDocumentService
 * Service for vendor document management (CRUD operations on own documents).
 * Route: api/vendor-portal/documents
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalDocumentService
 * Handles vendor document operations for the vendor portal.
 * Maps to /api/vendor-portal/documents endpoint
 */
export class VendorPortalDocumentService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalDocumentService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalDocumentService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all documents for the current vendor
     * GET /api/vendor-portal/documents
     */
    async getDocuments() {
        const response = await this.getDocumentsRaw();
        return response.data ?? [];
    }
    /**
     * Get all documents (raw response)
     */
    getDocumentsRaw() {
        return this.get();
    }
    // ==================== GET BY ID ====================
    /**
     * Get a specific document by ID
     * GET /api/vendor-portal/documents/{id}
     */
    async getDocument(id) {
        const response = await this.getDocumentRaw(id);
        if (!response.data) {
            throw new Error(`Document with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get a specific document (raw response)
     */
    getDocumentRaw(id) {
        return this.get(String(id));
    }
    // ==================== CREATE ====================
    /**
     * Create a new document for the current vendor
     * POST /api/vendor-portal/documents
     */
    async createDocument(data) {
        const response = await this.createDocumentRaw(data);
        if (!response.data) {
            throw new Error('Failed to create document');
        }
        return response.data;
    }
    /**
     * Create a new document (raw response)
     */
    createDocumentRaw(data) {
        return this.post('', data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing document
     * PUT /api/vendor-portal/documents/{id}
     */
    async updateDocument(id, data) {
        const response = await this.updateDocumentRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update document with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing document (raw response)
     */
    updateDocumentRaw(id, data) {
        return this.put(String(id), data);
    }
    // ==================== DELETE ====================
    /**
     * Delete a document
     * DELETE /api/vendor-portal/documents/{id}
     */
    async deleteDocument(id) {
        const response = await this.deleteDocumentRaw(id);
        return response.success;
    }
    /**
     * Delete a document (raw response)
     */
    deleteDocumentRaw(id) {
        return this.delete(String(id));
    }
    // ==================== DOCUMENT TYPES ====================
    /**
     * Get all available document types
     * GET /api/vendor-portal/documents/types
     */
    async getDocumentTypes() {
        const response = await this.getDocumentTypesRaw();
        return response.data ?? [];
    }
    /**
     * Get all document types (raw response)
     */
    getDocumentTypesRaw() {
        return this.get('types');
    }
    /**
     * Get only required document types
     * GET /api/vendor-portal/documents/types/required
     */
    async getRequiredDocumentTypes() {
        const response = await this.getRequiredDocumentTypesRaw();
        return response.data ?? [];
    }
    /**
     * Get required document types (raw response)
     */
    getRequiredDocumentTypesRaw() {
        return this.get('types/required');
    }
}
VendorPortalDocumentService.DEFAULT_SERVICE_PATH = 'vendors';
VendorPortalDocumentService.BASE_PATH = 'api/vendor-portal/documents';
//# sourceMappingURL=VendorPortalDocumentService.js.map